import React, { useState } from 'react';

interface ToolCardProps {
  toolName: string;
  toolCallId?: string;
  toolInput?: Record<string, any>;
  success?: boolean;
  duration_ms?: number;
  outputPreview?: string;
  isRunning?: boolean;
}

const TOOL_ICONS: Record<string, string> = {
  api_call: '\u{1F310}',      // globe
  bash: '\u{1F4BB}',          // laptop
  read_file: '\u{1F4C4}',     // page
  write_file: '\u{270F}\u{FE0F}',    // pencil
  edit_file: '\u{1F527}',     // wrench
  list_files: '\u{1F4C2}',    // folder
  web_search: '\u{1F50D}',    // magnifying glass
  web_fetch: '\u{1F517}',     // link
  validate_ttml: '\u{2705}',  // check mark
  ask_supervisor: '\u{1F4AC}', // speech bubble
};

const TOOL_LABELS: Record<string, string> = {
  api_call: 'API Call',
  bash: 'Shell Command',
  read_file: 'Read File',
  write_file: 'Write File',
  edit_file: 'Edit File',
  list_files: 'List Files',
  web_search: 'Web Search',
  web_fetch: 'Web Fetch',
  validate_ttml: 'Validate TTML',
  ask_supervisor: 'Ask Supervisor',
};

function formatInput(toolName: string, input: Record<string, any>): string {
  if (!input) return '';

  switch (toolName) {
    case 'api_call':
      return `${input.method || 'GET'} ${input.path || ''}`;
    case 'bash':
      return input.command || '';
    case 'read_file':
    case 'write_file':
    case 'edit_file':
      return input.path || '';
    case 'validate_ttml':
      return `${(input.ttml_content || '').length} chars`;
    case 'web_fetch':
      return input.url || '';
    case 'web_search':
      return input.query || '';
    case 'ask_supervisor':
      return input.question || '';
    default:
      return JSON.stringify(input).substring(0, 100);
  }
}

const AgentToolCard: React.FC<ToolCardProps> = ({
  toolName,
  toolCallId,
  toolInput,
  success,
  duration_ms,
  outputPreview,
  isRunning = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  const icon = TOOL_ICONS[toolName] || '\u{1F6E0}\u{FE0F}';
  const label = TOOL_LABELS[toolName] || toolName;
  const inputSummary = formatInput(toolName, toolInput || {});

  const statusColor = isRunning
    ? 'border-blue-400 dark:border-blue-500'
    : success
    ? 'border-green-400 dark:border-green-500'
    : 'border-red-400 dark:border-red-500';

  const bgColor = isRunning
    ? 'bg-blue-50 dark:bg-blue-900/20'
    : success
    ? 'bg-green-50 dark:bg-green-900/20'
    : 'bg-red-50 dark:bg-red-900/20';

  return (
    <div className={`my-2 border-l-3 ${statusColor} ${bgColor} rounded-r-lg overflow-hidden`}>
      <button
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="text-sm">{icon}</span>
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{label}</span>
        {inputSummary && (
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate flex-1 font-mono">
            {inputSummary}
          </span>
        )}
        {isRunning && (
          <span className="animate-spin text-xs">&#x23F3;</span>
        )}
        {duration_ms !== undefined && !isRunning && (
          <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
            {duration_ms < 1000 ? `${duration_ms}ms` : `${(duration_ms / 1000).toFixed(1)}s`}
          </span>
        )}
        <span className={`text-xs transition-transform ${expanded ? 'rotate-90' : ''}`}>&#x25B6;</span>
      </button>

      {expanded && (
        <div className="px-3 pb-2 border-t border-gray-200/50 dark:border-gray-700/50">
          {toolInput && (
            <div className="mt-2">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Input</div>
              <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto max-h-40 overflow-y-auto font-mono">
                {JSON.stringify(toolInput, null, 2)}
              </pre>
            </div>
          )}
          {outputPreview && (
            <div className="mt-2">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Output</div>
              <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto max-h-60 overflow-y-auto font-mono whitespace-pre-wrap">
                {outputPreview}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentToolCard;
